import { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  UploadCloud, 
  FileText, 
  Trash2, 
  Play, 
  Pause, 
  RotateCcw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Loader2,
  Briefcase
} from 'lucide-react';
import { cn } from "@/lib/utils";
import * as pdfjsLib from 'pdfjs-dist';
import * as mammoth from 'mammoth';
import { useBulkAnalysis } from './hooks/useBulkAnalysis';
import { JobSelector } from '../upload/JobSelector';
import type { BulkAnalysisFile, BulkAnalysisResult } from './types/bulk';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.js';

interface BulkAnalysisUploadProps {
  projectId?: string;
  projectRequirements?: {
    experience: string;
    skills: string[];
    education: string;
  };
  initialJobTitle?: string;
  initialJobDescription?: string;
  onAnalysisComplete?: (results: BulkAnalysisResult[]) => void;
}

export function BulkAnalysisUpload({ 
  projectId, 
  projectRequirements, 
  initialJobTitle,
  initialJobDescription,
  onAnalysisComplete 
}: BulkAnalysisUploadProps) {
  const [files, setFiles] = useState<BulkAnalysisFile[]>([]);
  const [jobTitle, setJobTitle] = useState(initialJobTitle || '');
  const [jobDescription, setJobDescription] = useState(initialJobDescription || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [jobDragActive, setJobDragActive] = useState(false);
  const [jobFileUploaded, setJobFileUploaded] = useState<string | null>(null);
  const [jobFileProcessing, setJobFileProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const jobFileInputRef = useRef<HTMLInputElement>(null);
  
  const { bulkAnalyze } = useBulkAnalysis();

  // Update state when initial values change
  useEffect(() => {
    if (initialJobTitle !== undefined) {
      setJobTitle(initialJobTitle);
    }
  }, [initialJobTitle]);

  useEffect(() => {
    if (initialJobDescription !== undefined) {
      setJobDescription(initialJobDescription);
    }
  }, [initialJobDescription]);

  // Enhanced file processing with multiple format support
  const processFiles = async (fileList: FileList) => {
    const newFiles: BulkAnalysisFile[] = [];
    
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const id = Date.now() + i;
      
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'application/rtf'
      ];
      
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const isValidType = allowedTypes.includes(file.type) || 
                         ['pdf', 'docx', 'txt', 'rtf'].includes(fileExtension || '');
      
      if (!isValidType) {
        newFiles.push({
          id,
          file,
          name: file.name,
          size: file.size,
          status: 'error',
          error: 'Unsupported file type. Please use PDF, DOCX, TXT, or RTF files.',
          cvText: '',
          extractedName: '',
          progress: 0
        });
        continue;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        newFiles.push({
          id,
          file,
          name: file.name,
          size: file.size,
          status: 'error',
          error: 'File too large. Maximum size is 10MB.',
          cvText: '',
          extractedName: '',
          progress: 0
        });
        continue;
      }
      
      // Add to processing queue
      newFiles.push({
        id,
        file,
        name: file.name,
        size: file.size,
        status: 'pending',
        cvText: '',
        extractedName: '',
        progress: 0
      });
    }
    
    setFiles(prev => [...prev, ...newFiles]);
    
    // Start text extraction for valid files
    extractTextFromFiles(newFiles.filter(f => f.status === 'pending'));
  };

  // Enhanced text extraction with better error handling
  const extractTextFromFiles = async (filesToProcess: BulkAnalysisFile[]) => {
    for (const fileData of filesToProcess) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === fileData.id 
            ? { ...f, status: 'extracting', progress: 25 }
            : f
        ));
        
        const arrayBuffer = await fileData.file.arrayBuffer();
        let extractedText = '';
        let extractedName = '';
        
        if (fileData.file.type === 'application/pdf' || fileData.name.toLowerCase().endsWith('.pdf')) {
          const result = await extractPdfText(arrayBuffer);
          extractedText = result.text;
          extractedName = result.name;
        } else if (fileData.file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                  fileData.name.toLowerCase().endsWith('.docx')) {
          const result = await extractDocxText(arrayBuffer);
          extractedText = result.text;
          extractedName = result.name;
        } else if (fileData.file.type === 'text/plain' || fileData.name.toLowerCase().endsWith('.txt')) {
          const result = await extractTxtText(arrayBuffer);
          extractedText = result.text;
          extractedName = result.name;
        } else if (fileData.file.type === 'application/rtf' || fileData.name.toLowerCase().endsWith('.rtf')) {
          const result = await extractRtfText(arrayBuffer);
          extractedText = result.text;
          extractedName = result.name;
        }
        
        if (extractedText.length < 100) {
          throw new Error('Could not extract meaningful text from file');
        }
        
        setFiles(prev => prev.map(f => 
          f.id === fileData.id 
            ? { 
                ...f, 
                status: 'ready', 
                cvText: extractedText, 
                extractedName: extractedName || fileData.name.replace(/\.[^/.]+$/, ""),
                progress: 100 
              }
            : f
        ));
        
      } catch (error) {
        setFiles(prev => prev.map(f => 
          f.id === fileData.id 
            ? { 
                ...f, 
                status: 'error', 
                error: error instanceof Error ? error.message : 'Failed to extract text',
                progress: 0 
              }
            : f
        ));
      }
    }
  };

  // PDF text extraction with name detection
  const extractPdfText = async (arrayBuffer: ArrayBuffer) => {
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      fullText += pageText + '\n';
    }
    
    const name = extractNameFromCV(fullText);
    return { text: fullText, name };
  };

  // DOCX text extraction with name detection
  const extractDocxText = async (arrayBuffer: ArrayBuffer) => {
    const result = await mammoth.extractRawText({ arrayBuffer });
    const text = result.value;
    const name = extractNameFromCV(text);
    return { text, name };
  };

  // TXT text extraction with name detection
  const extractTxtText = async (arrayBuffer: ArrayBuffer) => {
    const text = new TextDecoder('utf-8').decode(arrayBuffer);
    const name = extractNameFromCV(text);
    return { text, name };
  };

  // RTF text extraction with name detection
  const extractRtfText = async (arrayBuffer: ArrayBuffer) => {
    // Basic RTF parsing - remove RTF control words
    const text = new TextDecoder('utf-8').decode(arrayBuffer)
      .replace(/\\[a-z]+\d*\s?/g, ' ')
      .replace(/[{}]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const name = extractNameFromCV(text);
    return { text, name };
  };

  // Enhanced name extraction
  const extractNameFromCV = (text: string): string => {
    const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
    
    // Look for name patterns in first few lines
    for (let i = 0; i < Math.min(8, lines.length); i++) {
      const line = lines[i];
      
      // Skip email addresses and phone numbers
      if (line.includes('@') || /\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/.test(line)) {
        continue;
      }
      
      // Look for name patterns
      const namePattern = /^([A-Z][a-z]+(?:\s+[A-Z][a-z]*\.?)*(?:\s+[A-Z][a-z]+)+)$/;
      const match = line.match(namePattern);
      
      if (match && match[1].split(' ').length >= 2) {
        return match[1].trim();
      }
    }
    
    // Fallback to first line if it looks like a name
    const firstLine = lines[0] || '';
    const words = firstLine.split(' ').filter(Boolean);
    if (words.length >= 2 && words.length <= 4) {
      return firstLine;
    }
    
    return 'Unknown Candidate';
  };

  // Job Description File Processing
  const processJobDescriptionFile = async (file: File) => {
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/rtf'
    ];
    
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const isValidType = allowedTypes.includes(file.type) || 
                       ['pdf', 'docx', 'txt', 'rtf'].includes(fileExtension || '');
    
    if (!isValidType) {
      throw new Error('Unsupported file type. Please use PDF, DOCX, TXT, or RTF files.');
    }
    
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('File too large. Maximum size is 10MB.');
    }

    setJobFileProcessing(true);
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      let extractedText = '';
      
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        const result = await extractPdfText(arrayBuffer);
        extractedText = result.text;
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                file.name.toLowerCase().endsWith('.docx')) {
        const result = await extractDocxText(arrayBuffer);
        extractedText = result.text;
      } else if (file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt')) {
        const result = await extractTxtText(arrayBuffer);
        extractedText = result.text;
      } else if (file.type === 'application/rtf' || file.name.toLowerCase().endsWith('.rtf')) {
        const result = await extractRtfText(arrayBuffer);
        extractedText = result.text;
      }
      
      if (extractedText.length < 50) {
        throw new Error('Could not extract meaningful text from file');
      }
      
      // Extract job title from the beginning of the text
      const extractedTitle = extractJobTitleFromText(extractedText);
      
      setJobDescription(extractedText);
      if (extractedTitle && !jobTitle.trim()) {
        setJobTitle(extractedTitle);
      }
      setJobFileUploaded(file.name);
      
    } catch (error) {
      throw error;
    } finally {
      setJobFileProcessing(false);
    }
  };

  const extractJobTitleFromText = (text: string): string => {
    const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
    
    // Look for job title patterns in first few lines
    for (let i = 0; i < Math.min(3, lines.length); i++) {
      const line = lines[i];
      
      // Skip very short lines
      if (line.length < 5) continue;
      
      // Common job title patterns
      const jobTitlePatterns = [
        /(?:job title|position|role):\s*(.+)/i,
        /^(.+?)\s*(?:job|position|role)$/i,
        /^(.+?)\s*-\s*job description/i
      ];
      
      for (const pattern of jobTitlePatterns) {
        const match = line.match(pattern);
        if (match && match[1]) {
          return match[1].trim();
        }
      }
      
      // If first line looks like a title (not too long, capitalized)
      if (i === 0 && line.length < 80 && /^[A-Z]/.test(line)) {
        // Check if it contains job-related keywords
        const jobKeywords = ['engineer', 'manager', 'developer', 'analyst', 'specialist', 'coordinator', 'assistant', 'director', 'lead', 'senior', 'junior'];
        if (jobKeywords.some(keyword => line.toLowerCase().includes(keyword))) {
          return line;
        }
      }
    }
    
    return '';
  };

  // Job Description File Upload Handlers
  const handleJobDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setJobDragActive(true);
  }, []);

  const handleJobDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setJobDragActive(false);
  }, []);

  const handleJobDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setJobDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      const file = droppedFiles[0]; // Only take the first file
      try {
        await processJobDescriptionFile(file);
      } catch (error) {
        console.error('Job description file processing error:', error);
        // You might want to add a toast notification here
      }
    }
  }, []);

  const handleJobFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      try {
        await processJobDescriptionFile(file);
      } catch (error) {
        console.error('Job description file processing error:', error);
        // You might want to add a toast notification here
      }
      e.target.value = '';
    }
  }, []);

  const clearJobFile = () => {
    setJobFileUploaded(null);
    setJobDescription('');
    setJobTitle('');
  };

  // File upload handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      const fileList = new DataTransfer();
      droppedFiles.forEach(file => fileList.items.add(file));
      processFiles(fileList.files);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = '';
    }
  }, []);

  // File management
  const removeFile = (id: number) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const retryFile = (id: number) => {
    const file = files.find(f => f.id === id);
    if (file) {
      setFiles(prev => prev.map(f => 
        f.id === id 
          ? { ...f, status: 'pending', error: undefined, progress: 0 }
          : f
      ));
      extractTextFromFiles([file]);
    }
  };

  // Analysis execution
  const startBulkAnalysis = async () => {
    if (!jobTitle.trim() || !jobDescription.trim()) {
      return;
    }
    
    const readyFiles = files.filter(f => f.status === 'ready');
    if (readyFiles.length === 0) {
      return;
    }
    
    setIsProcessing(true);
    setIsPaused(false);
    
    try {
      const results = await bulkAnalyze({
        files: readyFiles,
        jobTitle,
        jobDescription,
        projectId
      });
      
      if (onAnalysisComplete) {
        onAnalysisComplete(results);
      }

    } catch (err: any) {
      console.error('Bulk analysis failed:', err);
    } finally {
      setIsProcessing(false);
      setIsPaused(false);
    }
  };

  const pauseAnalysis = () => {
    setIsPaused(true);
  };

  const resumeAnalysis = () => {
    setIsPaused(false);
  };

  const resetAnalysis = () => {
    setFiles([]);
    setIsProcessing(false);
    setIsPaused(false);
  };

  // File size formatter
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file status icon
  const getFileStatusIcon = (status: BulkAnalysisFile['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-slate-500" />;
      case 'extracting':
        return <Loader2 className="h-4 w-4 text-slate-600 animate-spin" />;
      case 'ready':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'analyzing':
        return <Loader2 className="h-4 w-4 text-slate-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <FileText className="h-4 w-4 text-slate-500" />;
    }
  };

  // Statistics
  const readyFiles = files.filter(f => f.status === 'ready');
  const errorFiles = files.filter(f => f.status === 'error');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Upload and Job Description */}
        <div className="lg:col-span-2 space-y-6">
          {/* File Upload Section */}
          <Card className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-900 font-bold tracking-tight">
                <UploadCloud className="h-5 w-5" />
                Upload CVs
              </CardTitle>
              <CardDescription className="text-slate-600">
                Drag and drop multiple CV files or click to browse. Supports PDF, DOCX, TXT, and RTF formats.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className={cn(
                  "border-2 border-dashed rounded-2xl p-6 text-center transition-colors cursor-pointer",
                  dragActive 
                    ? "border-slate-500 bg-slate-50" 
                    : "border-slate-300 hover:border-slate-400"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt,.rtf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <UploadCloud className="h-10 w-10 text-slate-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-900 dark:text-slate-100">
                  Drop your CV files here, or click to browse
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Support for PDF, DOCX, TXT, RTF • Max 10MB per file
                </p>
              </div>

              {files.length > 0 && (
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Uploaded Files ({files.length})</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetAnalysis}
                      disabled={isProcessing}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Clear All
                    </Button>
                  </div>
                  
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {files.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-xl"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {getFileStatusIcon(file.status)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {file.extractedName || file.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge variant={
                            file.status === 'ready' ? 'default' :
                            file.status === 'completed' ? 'secondary' :
                            file.status === 'error' ? 'destructive' :
                            'outline'
                          }>
                            {file.status}
                          </Badge>
                          
                          {file.status === 'error' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => retryFile(file.id)}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(file.id)}
                            disabled={isProcessing}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Job Description Section */}
          <Card className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/60">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-slate-900 font-bold tracking-tight">
                    <FileText className="h-5 w-5" />
                    Job Description
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    {projectId 
                      ? "Analyzing against project requirements" 
                      : "Enter manually or upload a job description file"
                    }
                  </CardDescription>
                </div>
                {!projectId && (
                  <JobSelector
                    onSelect={({ title, description }) => {
                      setJobTitle(title);
                      setJobDescription(description);
                      setJobFileUploaded(null);
                    }}
                  />
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* File Upload Area */}
              {!jobFileUploaded && (
                <div
                  className={cn(
                    "border-2 border-dashed rounded-2xl p-4 text-center transition-colors cursor-pointer",
                    jobDragActive 
                      ? "border-slate-500 bg-slate-50" 
                      : "border-slate-300 hover:border-slate-400"
                  )}
                  onDragOver={handleJobDragOver}
                  onDragLeave={handleJobDragLeave}
                  onDrop={handleJobDrop}
                  onClick={() => jobFileInputRef.current?.click()}
                >
                  <input
                    ref={jobFileInputRef}
                    type="file"
                    accept=".pdf,.docx,.txt,.rtf"
                    onChange={handleJobFileSelect}
                    className="hidden"
                  />
                  {jobFileProcessing ? (
                    <>
                      <Loader2 className="h-8 w-8 text-slate-400 mx-auto mb-2 animate-spin" />
                      <p className="text-sm font-medium text-slate-900">Processing job description...</p>
                    </>
                  ) : (
                    <>
                      <UploadCloud className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm font-medium text-slate-900">
                        Upload job description file
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        PDF, DOCX, TXT, RTF • Max 10MB
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Show uploaded file info */}
              {jobFileUploaded && (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-900">File uploaded successfully</p>
                      <p className="text-xs text-green-700">{jobFileUploaded}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearJobFile}
                    className="text-green-700 hover:text-green-900"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              )}

              <div>
                <Label htmlFor="jobTitle" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Job Title
                </Label>
                <Input
                  id="jobTitle"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g., Senior Software Engineer"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="jobDescription">Job Description</Label>
                <Textarea
                  id="jobDescription"
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste the complete job description here or upload a file above..."
                  className="mt-1 min-h-[200px]"
                />
                <div className="text-xs text-slate-500 mt-1">
                  💡 {projectId 
                    ? "Job description can be customized or you can use project defaults" 
                    : "Upload a file, use the \"Select Previous...\" button, or paste manually"
                  }
                </div>
              </div>
              
              {projectRequirements && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Project Context:</strong> CVs will be analyzed against this project's requirements including {projectRequirements.experience} experience level and skills like {projectRequirements.skills.slice(0, 3).join(', ')}.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Controls */}
        <div className="space-y-6">
          {/* Controls */}
          <Card>
            <CardHeader>
              <CardTitle>Analysis Controls</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!isProcessing ? (
                <Button
                  onClick={startBulkAnalysis}
                  disabled={readyFiles.length === 0 || !jobTitle.trim() || !jobDescription.trim()}
                  className="w-full"
                  size="lg"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start Bulk Analysis ({readyFiles.length} files)
                </Button>
              ) : (
                <div className="space-y-2">
                  {!isPaused ? (
                    <Button
                      onClick={pauseAnalysis}
                      variant="outline"
                      className="w-full"
                    >
                      <Pause className="h-4 w-4 mr-2" />
                      Pause Analysis
                    </Button>
                  ) : (
                    <Button
                      onClick={resumeAnalysis}
                      className="w-full"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Resume Analysis
                    </Button>
                  )}
                </div>
              )}
              
              {errorFiles.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {errorFiles.length} file(s) failed to process. Check the upload section for details.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}